import CoreBase from '../../../services/engines/CoreBase';

const _name = 'JInspectorHandler';

/**
 * JInspectorHandler — business logic for JInspector events.
 * Extends CoreBase for logError. Singleton export.
 * Params passed per method — no constructor deps, no stale closures.
 */
class JInspectorHandler extends CoreBase {
    _name = _name;

    /**
     * Handles keydown events — Enter saves, Escape cancels.
     * @param {KeyboardEvent} e
     * @param {boolean} isDirty
     * @param {Function} onSave
     * @param {Function} onCancel
     */
    handleKey = (e, isDirty, onSave, onCancel) => {
        try {
            if (e.key === 'Enter' && isDirty) onSave();
            if (e.key === 'Escape') onCancel();
        } catch (error) {
            this.logError('handleKey', error.message);
        }
    };

    /**
     * Computes inspector position and applies entry values on open.
     * @param {MouseEvent} e
     * @param {object|null} entry
     * @param {Function} applyEntry - fn(entry) resets all field state
     * @param {Function} setPos - fn({ top, left })
     * @param {Function} setVisible - fn(boolean)
     */
    handleShow = (e, entry, applyEntry, setPos, setVisible) => {
        try {
            applyEntry(entry);
            setPos({ top: e.clientY + 4, left: e.clientX - 200 });
            setVisible(true);
        } catch (error) {
            this.logError('handleShow', error.message);
        }
    };

    /**
     * Cancels all edits — resets fields and hides inspector.
     * @param {object|null} entry
     * @param {Function} applyEntry - fn(entry) resets all field state
     * @param {Function} setIsDirty - fn(boolean) from InspectorContext
     * @param {Function} setVisible - fn(boolean)
     */
    handleCancel = (entry, applyEntry, setIsDirty, setVisible) => {
        try {
            applyEntry(entry);
            setIsDirty(false);
            setVisible(false);
        } catch (error) {
            this.logError('handleCancel', error.message);
        }
    };

    /**
     * Saves current state — calls onSave prop and hides inspector.
     * @param {boolean} isDirty
     * @param {JInspectorState} current
     * @param {Function} onSave - prop fn({ title, stage, importanceId, prioId, type, cat, stateId, service, format, remark, isPinned })
     * @param {Function} setIsDirty - fn(boolean) from InspectorContext
     * @param {Function} setVisible - fn(boolean)
     */
    handleSave = (isDirty, current, onSave, setIsDirty, setVisible) => {
        try {
            if (!isDirty) return;
            if (typeof onSave === 'function') {
                onSave({
                    title: current.title,
                    stage: current.stage,
                    importanceId: current.importanceId,
                    prioId: current.prioId,
                    // CHANGED — current.type (was current.entryType), matches JInspectorState/entry naming
                    type: current.type,
                    cat: current.cat,
                    stateId: current.stateId,
                    // ADDED — move-to-topic support
                    service: current.service,
                    // ADDED — format field for card template identifier
                    format: current.format,
                    // ADDED — Journal.Remark, free text note on the entry
                    remark: current.remark,
                    // ADDED — editable Tid (template id)
                    tid: current.tid,
                    // CHANGED — current.isPinned (was current.pinned), matches JInspectorState/entry naming
                    isPinned: current.isPinned,
                });
            }
            setIsDirty(false);
            setVisible(false);
        } catch (error) {
            this.logError('handleSave', error.message);
        }
    };
}

const jInspectorHandler = new JInspectorHandler();
export default jInspectorHandler;
